/** @jsx isolateComponent */
import React, { useState } from 'react';

import { FormLabel } from '../../forms/FormLabel';
import { Interpolation } from '@emotion/core';
import { Animation, Colors, Typography } from '../../../theme';
import { useOverflow } from '../../../hooks/useOverflow';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { uniqueId } from '../../../utils/uniqueId';

type RenderInputOptions = {
  ariaDescribedBy?: string;
  id: string;
  isDisabled: boolean;
  isErrored: boolean;
  isRequired: boolean;
};

type RenderLabelProps = {
  htmlFor: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: string;
  requiredText?: React.ReactNode;
};

export type Props = {
  /**
   * Optional error message for the field. If it exists, the field will be
   * considered to have an error.
   */
  error?: string;
  /**
   * The number of help text lines to show (defaults to 1). Any extra text will be
   * hidden behind a "more" button.
   */
  helpTextLineClamp?: number;
  /**
   * `id` for the input field. If this isn't supplied then one will be generated
   * to properly link the `label` to the input.
   */
  inputId?: string;
  /**
   * Indicates whether the field is disabled.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the field is required.
   */
  isRequired?: boolean;
  /**
   * Text for the `label` element for this field.
   */
  label: string;
  /**
   * Function that returns the help text to be rendered beneath the input.
   */
  renderHelpText?: () => React.ReactNode;
  /**
   * Function that returns the rendered input field.
   */
  renderInput: (opts: RenderInputOptions) => React.ReactNode;
  /**
   * Function that returns the label to be rendered.
   */
  renderLabel?: (labelProps: RenderLabelProps) => React.ReactNode;
  /**
   * Optional text to render inside the required label.
   */
  requiredText?: React.ReactNode;
  /**
   * Indicates whether field label, required text, and help text
   * should only be shown when the input is focused.
   */
  revealOnFocus?: boolean;
};

type DerivedState = {
  isFocused: boolean;
};

const Styles = labelStyles('Field', {
  root: () => [
    {
      ...Typography.altText900,
      fontSize: 12,
      color: Colors.gray60,
      display: 'grid',
      gridGap: 1,
    },
  ],

  revealWrapper: (props: Props, derivedState: DerivedState) => [
    {
      // Since this is a Grid item and may contain `white-space: nowrap`
      // children, it needs to set `minWidth` so children truncate properly.
      minWidth: 0,
    },
    props.revealOnFocus && [
      withModifier('revealOnFocus', {
        opacity: 0,
        transition: `opacity ${Animation.transitionDuration} ease`,
      }),
      derivedState.isFocused &&
        withModifier('focused', {
          opacity: 1,
        }),
    ],
  ],

  helpText: [
    {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gridGap: 10,
      alignItems: 'start',
    },
  ],

  helpTextInner: ({
    isOpen,
    helpTextLineClamp,
  }: {
    isOpen: boolean;
    helpTextLineClamp: number;
  }) => [
    {
      display: '-webkit-box',
      overflow: 'hidden',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: helpTextLineClamp,
      // Without this truncation won't properly occur.
      '*': {
        display: 'inline',
        margin: 0,
      },
    },
    isOpen &&
      withModifier('open', {
        WebkitLineClamp: 'unset',
      }),
  ],

  helpTextToggle: {
    cursor: 'pointer',
    textDecoration: 'underline',
    textTransform: 'lowercase' as 'lowercase',
    width: 31, // So that the help text don't get longer and shorter
    '&:hover, &:focus': {
      color: Colors.ocean,
    },
  },

  error: {
    color: Colors.error,
  },
});

type HelpTextProps = {
  children: React.ReactNode;
  helpTextLineClamp: number;
};

/**
 * Component to render help text which can be expanded and collapsed.
 */
const HelpText = ({ children, helpTextLineClamp }: HelpTextProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setOverflowingNode] = useOverflow();
  // Only render toggle if it's overflowing.
  const toggle = isOverflowing && (
    <button
      aria-hidden="true"
      css={Styles.helpTextToggle}
      onClick={() => setIsOpen(!isOpen)}
      type="button"
    >
      {isOpen ? 'Less' : 'More'}
    </button>
  );
  return (
    <div css={Styles.helpText}>
      <div
        css={
          Styles.helpTextInner({ isOpen, helpTextLineClamp }) as Interpolation
        }
        ref={(node) => setOverflowingNode(node as HTMLElement)}
      >
        <div>{children}</div>
      </div>
      {toggle}
    </div>
  );
};

/**
 * Wraps an input field and renders a label and optional help text.
 */
export const Field = (props: Props) => {
  const [id] = useState(props.inputId || uniqueId('Field-'));
  const [isFocused, setFocus] = useState(false);
  const helpText = props.renderHelpText && props.renderHelpText();
  const describedById = `${id}-description`;
  const inputOptions = {
    ariaDescribedBy: helpText || props.error ? describedById : undefined,
    id,
    isDisabled: !!props.isDisabled,
    isErrored: !!props.error,
    isRequired: !!props.isRequired,
  };
  const derivedState = {
    isFocused,
  };

  const { renderLabel = DefaultLabel, helpTextLineClamp = 1 } = props;

  return (
    <div
      css={Styles.root()}
      onBlur={() => setFocus(false)}
      onFocus={() => setFocus(true)}
    >
      <div css={Styles.revealWrapper(props, derivedState)}>
        {renderLabel({
          htmlFor: id,
          isDisabled: props.isDisabled,
          isRequired: props.isRequired,
          label: props.label,
          requiredText: props.requiredText,
        })}
      </div>
      <div>{props.renderInput(inputOptions)}</div>
      <div css={Styles.revealWrapper(props, derivedState)} id={describedById}>
        {props.error && <div css={Styles.error}>{props.error}</div>}
        {helpText && (
          <HelpText helpTextLineClamp={helpTextLineClamp}>{helpText}</HelpText>
        )}
      </div>
    </div>
  );
};

type DefaultLabelProps = Pick<Props, 'isDisabled' | 'isRequired' | 'label'> & {
  htmlFor: string;
  requiredText?: React.ReactNode;
};

function DefaultLabel({
  htmlFor,
  isDisabled,
  isRequired,
  label,
  requiredText,
}: DefaultLabelProps) {
  return (
    <FormLabel
      htmlFor={htmlFor}
      isDisabled={isDisabled}
      isRequired={isRequired}
      layout="text-only"
      requiredText={requiredText}
      size="small"
    >
      {label}
    </FormLabel>
  );
}

Field.defaultProps = {
  renderLabel: DefaultLabel,
  helpTextLineClamp: 1,
};
